import { createSignal, useContext, type Component, type JSX } from 'solid-js'
import { PerspectiveCamera, Vector2, WebGLRenderer } from 'three'
import type { Engine } from '../../lib/core/engine/Engine.type'
import { getEngineThreeMainCamera } from '../../lib/three/fn/getSetEngineThreeMainCamera'
import { getEngineThreeMainRenderer } from '../../lib/three/fn/getSetEngineThreeMainRenderer'
import useMainScene from '../../lib/three/fn/useMainScene'
import { getDeviceUserPreference } from '../../rx/shared/device/userPreference/deviceUserPreferencesSignal'
import EngineContext from '../EngineContext'
import { RESOLUTION_480p } from './resolutions'

interface RecordVideoButtonProps {
  filename?: string
  children?: JSX.Element
  class?: string
  durationMs?: number
  fps?: number
}

const RecordVideoButton: Component<RecordVideoButtonProps> = (props) => {
  const engine: Engine = useContext(EngineContext)
  
  const scene = useMainScene(engine)
  const renderer = getEngineThreeMainRenderer(engine)
  const originalCamera = getEngineThreeMainCamera(engine)

  if (!renderer || !(originalCamera instanceof PerspectiveCamera)) {
    // eslint-disable-next-line solid/components-return-once
    return <div>Renderer or compatible camera not found</div>
  }
  
  const camera = originalCamera as PerspectiveCamera
  
  const [isRecording, setIsRecording] = createSignal(false)
  let overlayElement: HTMLDivElement | null = null
  
  const handleRecord = async () => {
    if (!renderer || !scene || !camera) {
      console.error('Renderer, scene, or camera not found')
      return
    }
    const durationMs = props.durationMs || 5000 // 5 seconds default
    const fps = props.fps || 30
    const totalFrames = Math.ceil((durationMs / 1000) * fps)
  
    // Store original renderer settings
    const originalPixelRatio = renderer.getPixelRatio()
    const originalSize = new Vector2()
    renderer.getSize(originalSize)
    
    const { width, height } = RESOLUTION_480p
    
    try {
      setIsRecording(true)
      
      // Add overlay to main canvas
      if (renderer.domElement.parentElement) {
        // Ensure canvas has proper positioning context
        const parent = renderer.domElement.parentElement
        parent.style.position = parent.style.position || 'relative'
        
        // Create and style overlay
        overlayElement = document.createElement('div')
        Object.assign(overlayElement.style, {
          position: 'absolute',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          pointerEvents: 'none',
          zIndex: '9999',
          borderRadius: parent.style.borderRadius || '0',
          boxSizing: 'border-box'
        })
        
        // Ensure canvas has a z-index
        renderer.domElement.style.position = 'relative'
        renderer.domElement.style.zIndex = '1'
        
        // Add overlay after a small delay to ensure rendering
        requestAnimationFrame(() => {
          parent.appendChild(overlayElement!)
          console.log('Overlay added to canvas parent')
        })
      }
      
      // Create a new renderer for recording
      const tempRenderer = new WebGLRenderer({
        canvas: document.createElement('canvas'),
        antialias: true,
        alpha: true,
        preserveDrawingBuffer: true
      })
      tempRenderer.setPixelRatio(1)
      tempRenderer.setSize(width, height, false)
      tempRenderer.shadowMap.enabled = renderer.shadowMap.enabled
      tempRenderer.shadowMap.type = renderer.shadowMap.type
      tempRenderer.outputColorSpace = renderer.outputColorSpace
      
      // Create a temporary camera with the same properties as the current camera
      const tempCamera = camera.clone()
      tempCamera.aspect = width / height
      tempCamera.fov = getDeviceUserPreference().camera_fov
      tempCamera.updateProjectionMatrix()
      tempCamera.layers.disable(1)
      
      // Create a canvas for the video frames
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      
      if (!ctx) {
        throw new Error('Could not get 2D context')
      }
      
      // Create a MediaRecorder to record the frames
      const stream = canvas.captureStream(fps)
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp9',
        videoBitsPerSecond: 25_000_000 // 25 Mbps for high quality 4K
      })
      
      const chunks: BlobPart[] = []
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data)
        }
      }
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' })
        const url = URL.createObjectURL(blob)
        
        const a = document.createElement('a')
        a.href = url
        a.download = props.filename || 'recording.webm'
        a.click()
        
        URL.revokeObjectURL(url)
        setIsRecording(false)
        
        // Remove overlay
        if (overlayElement) {
          if (overlayElement.parentElement) {
            overlayElement.parentElement.removeChild(overlayElement)
          }
          overlayElement = null
          console.log('Overlay removed')
        }
      }
      
      // Start recording
      mediaRecorder.start()
      
      // Record frames
      let frameCount = 0
      const startTime = performance.now()
      
      const recordFrame = () => {
        if (frameCount >= totalFrames) {
          mediaRecorder.stop()
          return
        }
        
        // Render the frame using our temp renderer and camera
        tempRenderer.render(scene, tempCamera)
        
        // Draw to our canvas context
        ctx.drawImage(tempRenderer.domElement, 0, 0, width, height)
        
        // Request next frame
        frameCount++
        const elapsed = performance.now() - startTime
        const targetTime = (frameCount / fps) * 1000
        const delay = Math.max(0, targetTime - elapsed)
        
        setTimeout(recordFrame, delay)
      }
      
      // Start recording frames
      recordFrame()
      
      // Auto-stop after duration
      setTimeout(() => {
        if (mediaRecorder.state === 'recording') {
          mediaRecorder.stop()
        }
      }, durationMs + 1000) // Add some buffer time
      
    } catch (error) {
      console.error('Error recording video:', error)
    } finally {
      try {
        // Restore original renderer settings
        renderer.setPixelRatio(originalPixelRatio)
        renderer.setSize(originalSize.width, originalSize.height, false)
        camera.aspect = originalSize.width / originalSize.height
        camera.updateProjectionMatrix()
        
        // Re-render with original settings
        renderer.render(scene, camera)
      } finally {
        // Ensure overlay is removed even if there's an error
        if (overlayElement && overlayElement.parentElement) {
          overlayElement.parentElement.removeChild(overlayElement)
          overlayElement = null
        }
      }
    }
  }
  
  return (
    <button 
      onClick={handleRecord}
      disabled={isRecording()}
      class={`px-4 py-2 ${isRecording() ? 'bg-gray-500' : 'bg-red-600 hover:bg-red-700'} text-white rounded ${props.class || ''}`}
      aria-label={isRecording() ? 'Recording in progress...' : 'Record video'}
    >
      {isRecording() ? 'Recording...' : (props.children || 'Record Video')}
    </button>
  )
}

export default RecordVideoButton
